/**
 * src/features/billing/ui/PlanBadge.tsx
 *
 * Ambient header badge showing plan tier. Display-only — no click handler.
 * Renders null for free users (no visual clutter), a blue Trial pill during
 * trialing, and a green Pro pill otherwise.
 *
 * T-08-16: plan is UX state only — this badge never gates access.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

export interface PlanBadgeProps {
  plan: 'free' | 'pro';
  planStatus?: 'active' | 'trialing' | 'canceled' | 'past_due' | string;
  // Phase 27 — UX-only: shows a purple bonus pill when > 0 (never used for authorization).
  // Priority slot 3 (after trialing check, before Pro fallback) — see rendering order below.
  rewardDaysRemaining?: number;
}

export function PlanBadge({ plan, planStatus, rewardDaysRemaining }: PlanBadgeProps): React.ReactElement | null {
  const { t } = useTranslation();

  // Priority slot 1 — free plan: no badge
  if (plan !== 'pro') {
    return null;
  }

  // Priority slot 2 — trialing: blue Trial pill (unchanged)
  if (planStatus === 'trialing') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
        {t('billing.trial_badge', 'Trial')}
      </span>
    );
  }

  // Priority slot 3 — active referral reward: purple bonus pill (Phase 27, additive)
  if (rewardDaysRemaining !== undefined && rewardDaysRemaining > 0) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
        {t('referral.badge_bonus', 'Pro (bonus: {{n}} days)', { n: rewardDaysRemaining })}
      </span>
    );
  }

  // Priority slot 4 — Pro (active/other): green Pro pill (unchanged)
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      {t('billing.pro_badge', 'Pro')}
    </span>
  );
}
